import { useEffect, useRef } from "react";

interface UseStickyHeaderProps {
  headerRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const NAVBAR_HEIGHT = 50;

export const useStickyHeader = ({
  headerRef,
  containerRef,
}: UseStickyHeaderProps) => {
  const isStickyRef = useRef(false);
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const rafIdRef = useRef<number>(0);

  useEffect(() => {
    const headerEl = headerRef.current;
    const container = containerRef.current;
    if (!headerEl || !container) return;

    const tableContainer = container.querySelector(
      ".table-container"
    ) as HTMLElement | null;
    if (!tableContainer) return;

    // Create placeholder element to prevent layout shift
    const placeholder = document.createElement("div");
    placeholder.style.display = "none";
    placeholder.style.width = "100%";
    headerEl.parentNode?.insertBefore(placeholder, headerEl.nextSibling);
    placeholderRef.current = placeholder;

    const applyStyles = (sticky: boolean, headerHeight: number) => {
      if (sticky) {
        placeholder.style.display = "block";
        placeholder.style.height = `${headerHeight}px`;
        headerEl.style.position = "fixed";
        headerEl.style.top = `${NAVBAR_HEIGHT}px`;
        headerEl.style.zIndex = "100";
        headerEl.style.backgroundColor = "white";
        headerEl.style.width = `${tableContainer.clientWidth}px`;
        headerEl.style.overflow = "hidden";
      } else {
        placeholder.style.display = "none";
        headerEl.style.position = "";
        headerEl.style.top = "";
        headerEl.style.zIndex = "";
        headerEl.style.width = "";
        headerEl.style.overflow = "";
      }
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const shouldStick =
          rect.top < NAVBAR_HEIGHT && rect.bottom > NAVBAR_HEIGHT + 100;

        if (shouldStick !== isStickyRef.current) {
          const headerHeight = headerEl.offsetHeight;
          isStickyRef.current = shouldStick;
          applyStyles(shouldStick, headerHeight);
        }
      });
    };

    const handleTableScroll = () => {
      if (isStickyRef.current) {
        headerEl.scrollLeft = tableContainer.scrollLeft;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    tableContainer.addEventListener("scroll", handleTableScroll, {
      passive: true,
    });
    handleScroll();

    return () => {
      cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener("scroll", handleScroll);
      tableContainer.removeEventListener("scroll", handleTableScroll);
      placeholder.remove();
    };
    // containerRef and headerRef are stable useRef objects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isStickyRef };
};
